import { FileText, Map, Building, MessageCircle, FolderOpen, DollarSign, FileSearch } from 'lucide-react';
import { checkAnyPermissions, checkAnyPermissionsFromSubtramite, PERMISSIONS } from '@/auth/permissions';
import CommentsTab from '../CommentsTab';
import DocumentsTab from '../DocumentsTab';
import Presupuestos from '../Pages/Subtramites/Presupuestos';

export interface TabConfig {
  id: string;
  label: string;
  icon: any;
  visible: boolean;
  canActivate?: boolean;
  items?: TabItem[];
  Component?: React.ComponentType<{ tab: TabConfig; tramite: any }>;
}

export interface TabItem {
  title: string;
  disabled: boolean;
  href: string;
  visible: boolean;
  key: string;
  canActivate?: boolean;
  canChangeName?: boolean;
}

export const createTabsConfig = (userPermissions: string[], user: any, tramite: any): TabConfig[] => [
  {
    id: 'comentarios',
    label: 'Comentarios',
    icon: MessageCircle,
    Component: CommentsTab,
    visible: checkAnyPermissions(userPermissions, [
      PERMISSIONS.MANAGE_COMMENTS.name,
      PERMISSIONS.VIEW_COMMENTS.name
    ]),
  },
  {
    id: 'documentos',
    label: 'Documentos',
    icon: FolderOpen,
    Component: DocumentsTab,
    visible: checkAnyPermissions(userPermissions, [
      PERMISSIONS.MANAGE_DOCUMENTS.name,
      PERMISSIONS.VIEW_DOCUMENTS.name
    ]),
  },
  {
    id: 'certificados',
    label: 'Certificados',
    icon: FileText,
    visible: checkAnyPermissions(userPermissions, [
      PERMISSIONS.VIEW_CERTIFICADOS_FILES.name,
    ]),
    items: [
      {
        title: 'Certificado catastral',
        disabled: !tramite.catastral?.habilitado,
        href: '/catastral',
        visible: checkAnyPermissionsFromSubtramite(
          user,
          PERMISSIONS.VIEW_CERTIFICADOS_FILES.name,
          tramite,
          'catastral',
          true
        ),
        key: 'catastral'
      },
      {
        title: 'Certificado reserva de prioridad',
        disabled: !tramite.reserva?.habilitado,
        href: '/reserva',
        visible: checkAnyPermissionsFromSubtramite(
          user,
          PERMISSIONS.VIEW_CERTIFICADOS_FILES.name,
          tramite,
          'reserva',
          true
        ),
        key: 'reserva'
      },
      {
        title: tramite.otros?.nombre || 'Otros',
        disabled: !tramite.otros?.habilitado,
        href: '/otros',
        visible: true,
        key: 'otros'
      }
    ]
  },
  {
    id: 'planos',
    label: 'Planos',
    icon: Map,
    visible: checkAnyPermissions(userPermissions, [
      PERMISSIONS.VIEW_PLANOS_FILES.name,
    ]),
    items: [
      {
        title: 'Plano de mensura',
        disabled: !tramite.planomensura?.habilitado,
        href: '/planomensura',
        visible: checkAnyPermissionsFromSubtramite(
          user,
          PERMISSIONS.VIEW_PLANOS_FILES.name,
          tramite,
          'planomensura',
          true
        ),
        key: 'planomensura'
      },
      {
        title: 'Plano de relevamiento',
        disabled: !tramite.planorelevamiento?.habilitado,
        href: '/planorelevamiento',
        visible: checkAnyPermissionsFromSubtramite(
          user,
          PERMISSIONS.VIEW_PLANOS_FILES.name,
          tramite,
          'planorelevamiento',
          true
        ),
        key: 'planorelevamiento'
      },
      {
        title: tramite.otros?.nombre || 'Otros',
        disabled: !tramite.otros?.habilitado,
        href: '/otros',
        visible: true,
        key: 'otros'
      }
    ]
  },
  {
    id: 'tramites',
    label: 'Trámites',
    icon: Building,
    visible: checkAnyPermissions(userPermissions, [
      PERMISSIONS.VIEW_TRAMITES_FILES.name,
    ]),
    items: [
      {
        title: 'ATM',
        disabled: !tramite.atm?.habilitado,
        href: '/atm',
        visible: checkAnyPermissionsFromSubtramite(
          user,
          PERMISSIONS.VIEW_TRAMITES_FILES.name,
          tramite,
          'atm',
          true
        ),
        key: 'atm'
      },
      {
        title: 'Municipalidad',
        disabled: !tramite.municipalidad?.habilitado,
        href: '/municipalidad',
        visible: checkAnyPermissionsFromSubtramite(
          user,
          PERMISSIONS.VIEW_TRAMITES_FILES.name,
          tramite,
          'municipalidad',
          true
        ),
        key: 'municipalidad'
      },
      {
        title: 'Vialidad',
        disabled: !tramite.vialidad?.habilitado,
        href: '/vialidad',
        visible: checkAnyPermissionsFromSubtramite(
          user,
          PERMISSIONS.VIEW_TRAMITES_FILES.name,
          tramite,
          'vialidad',
          true
        ),
        key: 'vialidad'
      },
      {
        title: 'Aguas',
        disabled: !tramite.aguas?.habilitado,
        href: '/aguas',
        visible: checkAnyPermissionsFromSubtramite(
          user,
          PERMISSIONS.VIEW_TRAMITES_FILES.name,
          tramite,
          'aguas',
          true
        ),
        key: 'aguas'
      },
      {
        title: 'Expensas',
        disabled: !tramite.expensas?.habilitado,
        href: '/expensas',
        visible: checkAnyPermissionsFromSubtramite(
          user,
          PERMISSIONS.VIEW_TRAMITES_FILES.name,
          tramite,
          'expensas',
          true
        ),
        key: 'expensas'
      },
      {
        title: 'Irrigación',
        disabled: !tramite.irrigacion?.habilitado,
        href: '/irrigacion',
        visible: checkAnyPermissionsFromSubtramite(
          user,
          PERMISSIONS.VIEW_TRAMITES_FILES.name,
          tramite,
          'irrigacion',
          true
        ),
        key: 'irrigacion'
      },
      {
        title: tramite.otros?.nombre || 'Otros',
        disabled: !tramite.otros?.habilitado,
        href: '/otros',
        visible: true,
        key: 'otros'
      }
    ]
  },
  {
    id: 'estudio-titulo',
    label: 'Estudio de Título',
    icon: FileSearch,
    visible: checkAnyPermissions(userPermissions, [
      PERMISSIONS.VIEW_ESTUDIO_TITULO_FILES.name,
    ]),
    canActivate: checkAnyPermissions(userPermissions, [
      PERMISSIONS.UPLOAD_ESTUDIO_TITULO_FILES.name,
    ]),
    items: [
      {
        title: 'Resultado',
        disabled: !tramite.estudiotitulo?.habilitado,
        href: '/estudiotitulo',
        visible: checkAnyPermissionsFromSubtramite(
          user,
          PERMISSIONS.VIEW_ESTUDIO_TITULO_FILES.name,
          tramite,
          'estudiotitulo',
          true
        ),
        canActivate: checkAnyPermissions(userPermissions, [
          PERMISSIONS.UPLOAD_ESTUDIO_TITULO_FILES.name,
        ]),
        key: 'estudiotitulo'
      },
      {
        title: tramite.otros?.nombre || 'Documentos',
        disabled: !tramite.otros?.habilitado,
        canChangeName: false,
        href: '/otros',
        visible: checkAnyPermissionsFromSubtramite(
          user,
          PERMISSIONS.VIEW_ESTUDIO_TITULO_FILES.name,
          tramite,
          'estudiotitulo',
          true
        ),
        key: 'otros',
      }
    ]
  },
  {
    id: 'presupuestos',
    label: 'Presupuesto',
    icon: DollarSign,
    visible: checkAnyPermissions(userPermissions, [
      PERMISSIONS.VIEW_PRESUPUESTOS.name
    ]),
    canActivate: checkAnyPermissions(userPermissions, [
      PERMISSIONS.VIEW_PRESUPUESTOS.name
    ]),
    Component: Presupuestos
  }
];